<template>
    <div class="avg-calls-duration-form">
        <div class="w-full">
            <RedesignFormItem
                :label="$t('widget.countersToDisplay')"
            >
                <RedesignBaseSelect
                    v-model="widgetLayoutModel.showCounter"
                    :data="availableCountersOptions"
                    :config="{
                        valueKey: 'value',
                        labelKey: 'label'
                    }"
                    :multiple="false"
                    full-width
                />
            </RedesignFormItem>
        </div>
        <div>
            <RedesignFormItem
                :label="$t('widget.contentTitle')"
            >
                <RedesignBaseInput v-model="contentTitleModel" />
            </RedesignFormItem>
        </div>
        <div class="mb-5 space-y-1.5">
            <RedesignCheckbox
                v-model="widgetLayoutModel.showText"
            >
                {{ $t('status.show.text') }}
            </RedesignCheckbox>
            <RedesignCheckbox
                v-model="widgetLayoutModel.displayBorder"
            >
                {{ $t('status.display.border') }}
            </RedesignCheckbox>
        </div>
        <div class="mb-5">
            <WidgetInnerAlignment
                :content="widgetContentAlignment"
                :layout="widgetContentLayout"
                @update:content="$evt => widgetContentAlignment = $evt"
                @update:layout="$evt => widgetContentLayout = $evt"
            />
        </div>
        <div class="mb-5">
            <WidgetCardLayoutConfigs v-model="widgetLayoutModel" />
        </div>
        <div
            v-if="widgetTime?.type"
            class="mb-5"
        >
            <BaseCollapseItem
                :title="$t('settings.time.frame')"
                name="widget-time-frame"
                class="simple-collapse"
            >
                <RedesignWidgetTimeFrame
                    v-model="widgetTimeModel"
                    :time-frame-type="widgetTimeModel.type"
                    :widget-time-options="widgetTimeOptions"
                >
                    <template #frame-types>
                        <RedesignRadioGroup
                            v-model="widgetTimeModel.type"
                            class="radio-groups mb-4"
                        >
                            <RedesignRadioButton
                                v-for="type in createWidgetTimeTypes"
                                :key="type.value"
                                :label="type.value"
                            >
                                {{ type.label }}
                            </RedesignRadioButton>
                        </RedesignRadioGroup>
                    </template>
                </RedesignWidgetTimeFrame>
            </BaseCollapseItem>
        </div>
        <div
            v-if="widgetConfigMapped.filters.length"
            class="mb-5"
        >
            <BaseCollapseItem
                :title="$t('settings.filters')"
                name="filters"
                class="simple-collapse"
            >
                <div
                    v-if="autoCompletes.length"
                    class="flex flex-col text-main-sm items-start main-text-color gap-2 mb-5"
                >
                    {{ $t('tooltip.refresh.entities.list') }}
                    <RefreshButton
                        :disabled="loadEntitiesList"
                        :loading="loadEntitiesList"
                        @click="refreshEntitiesList"
                    />
                </div>
                <div
                    v-for="(filter, index) in widgetConfigMapped.filters"
                    :key="index"
                >
                    <RedesignWidgetConfigEntityTree
                        v-if="isAutoComplete(filter)"
                        :key="`filter-tree-${filter.ParameterID}`"
                        :value="getWidgetConfig(filter)"
                        class="mb-4"
                        @update:value="updateFilterItem"
                    />
                    <RedesignWidgetConfigSelect
                        v-if="isMultiSelect(filter)"
                        :key="`filter-select-${filter.ParameterID}`"
                        :value="getWidgetConfig(filter)"
                        @update:value="updateFilterItem"
                    />
                </div>
            </BaseCollapseItem>
        </div>
        <div
            v-if="widgetConfigMapped.otherFilters.length"
        >
            <BaseCollapseItem
                :title="$t('settings.other.filters')"
                name="otherFilters"
                class="simple-collapse"
            >
                <div
                    v-for="(filter, index) in widgetConfigMapped.otherFilters"
                    :key="`other-filter-${index}`"
                    class="mb-4"
                >
                    <RedesignWidgetOtherFilters
                        :value="getWidgetConfig(filter)"
                        :parameter-type="filter.ParameterType"
                        @update:value="updateFilterItem"
                    />
                </div>
            </BaseCollapseItem>
        </div>
    </div>
</template>

<script setup lang="ts">
import { WidgetConfig, WidgetLayout, WidgetTime } from '@/types/dashboard'
import { availableCounters, AVERAGE_DURATION_ALL_CALLS } from '@/enum/queueCounters'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import { useVModel } from '@vueuse/core'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import { computed } from 'vue'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import BaseCollapseItem from '@/components/RedesignBaseComponents/Collapse/BaseCollapseItem.vue'
import { widgetTimeOptions, widgetTimeTypes } from '@/enum/widgetTimeOptions'
import {
    getWidgetConfigByTemplateId,
    getWidgetConfigMapped,
    isAutoComplete,
    isMultiSelect
} from '@/components/RedesignComponents/Widgets/widget.helper'
import RedesignRadioButton from '@/components/RedesignBaseComponents/Inputs/RedesignRadioButton.vue'
import RedesignWidgetConfigSelect from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelect.vue'
import RedesignWidgetTimeFrame from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetTimeFrame.vue'
import RefreshButton from '@/components/RefreshButton.vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignWidgetOtherFilters from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetOtherFilters.vue'
import WidgetCardLayoutConfigs
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/WidgetCardLayoutConfigs.vue'
import WidgetInnerAlignment from '@/components/RedesignComponents/Widgets/Common/WidgetInnerAlignment.vue'
import i18n from '@/i18n'
import RedesignWidgetConfigEntityTree
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigEntityTree.vue'

interface IProps {
    value: WidgetLayout
    widgetTime: WidgetTime
    widgetConfig: WidgetConfig[]
    templateId: number
    loadEntitiesList?: boolean
}

const props = defineProps<IProps>()
const emit = defineEmits<{
    (e: 'input', pl: WidgetLayout): void
    (e: 'update:config', pl: WidgetConfig): void
    (e: 'refreshEntitiesList'): void
    (e: 'update:widgetTime', pl: WidgetTime): void
}>()


const widgetLayoutModel = useVModel(props, 'value', emit, {
    eventName: 'input',
    passive: true
})
const showCounterKey = computed(() => {
    return widgetLayoutModel.value.showCounter || AVERAGE_DURATION_ALL_CALLS
})

const defaultContentTitle = computed(() => {
    const entry = availableCounters.find((counter) => counter.value === showCounterKey.value)
    return entry ? i18n.t(entry.label).toString() : showCounterKey.value
})

const widgetTimeModel = computed({
    get () {
        return props.widgetTime
    },
    set (val) {
        emit('update:widgetTime', val)
    }
})

const contentTitleModel = computed({
    get () {
        const key = showCounterKey.value
        const title = widgetLayoutModel.value?.ContentTitle

        if (title && (title[key] || title[key] === '')) {
            return title[key] as string
        }

        return defaultContentTitle.value
    },
    set (val) {
        const key = showCounterKey.value

        if (defaultContentTitle.value === val) {
            return
        }
        
        const ContentTitle = widgetLayoutModel.value.ContentTitle || {}

        widgetLayoutModel.value = {
            ...widgetLayoutModel.value,
            ContentTitle: {
                ...ContentTitle,
                [key]: String(val)
            }
        }
    }
})
const widgetContentAlignment = computed({
    get () {
        return widgetLayoutModel.value?.contentAlignment === undefined ? 'center' : widgetLayoutModel.value.contentAlignment
    },
    set (val) {
        if (widgetLayoutModel.value.contentAlignment === undefined) {
            widgetLayoutModel.value = {
                ...widgetLayoutModel.value,
                contentAlignment: 'center'
            }
        }
        widgetLayoutModel.value.contentAlignment = val
    }
})
const widgetContentLayout = computed({
    get () {
        return widgetLayoutModel.value?.contentLayout === undefined ? 'default' : widgetLayoutModel.value.contentLayout
    },
    set (val) {
        if (widgetLayoutModel.value.contentLayout === undefined) {
            widgetLayoutModel.value = {
                ...widgetLayoutModel.value,
                contentLayout: 'default'
            }
        }
        widgetLayoutModel.value.contentLayout = val
    }
})
const widgetConfigMapped = computed(() => {
    return getWidgetConfigMapped(props.widgetConfig)
})
const availableCountersOptions = computed(() => {
    return availableCounters.map(el => {
        return {
            label: i18n.t(el.label),
            value: el.value
        }
    })
})
const autoCompletes = computed(() => {
    return props.widgetConfig.filter(config => isAutoComplete(config))
})
const createWidgetTimeTypes = computed(() => {
    return widgetTimeTypes
        .map(el => {
            return {
                label: i18n.t(el.text),
                value: el.label
            }
        })
})

/* Methods */
function getWidgetConfig (config: WidgetConfig) {
    return getWidgetConfigByTemplateId(config, props.templateId)
}

function updateFilterItem (filterData: WidgetConfig) {
    emit('update:config', filterData)
}

function refreshEntitiesList () {
    emit('refreshEntitiesList')
}
</script>

